/**
 * usePermissions — React hook + helpers for RBAC client-side.
 *
 * Fetches the current user's permissions from `/api/users/me/permissions`
 * once per session (cached). Components consume it via:
 *
 *   const { can, role, roleLabel, loading } = usePermissions();
 *   if (can("manage_night_audit")) { ... }
 *
 * Or use the <PermGate perm="..."> component to hide/replace UI atoms.
 *
 * The auth token is shared with the global axios instance, so this hook
 * works inside any AuthProvider-wrapped tree.
 *
 * SSOT: the backend `permissions_catalog.py` is the single source of truth.
 * NEVER hardcode role→permission maps in the UI.
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Module-level cache so multiple components share one fetch per session.
let _cachedPerms = null;
let _cachedUserId = null;
let _pendingPromise = null;

export function clearPermissionsCache() {
  _cachedPerms = null;
  _cachedUserId = null;
  _pendingPromise = null;
}

export function usePermissions() {
  const { user } = useAuth();
  const [state, setState] = useState({
    loading: true,
    permissions: new Set(),
    role: null,
    roleLabel: null,
  });

  const load = useCallback(async () => {
    if (!user?.id) {
      setState({ loading: false, permissions: new Set(), role: null, roleLabel: null });
      return;
    }

    // Reuse cache if it belongs to the same user
    if (_cachedPerms && _cachedUserId === user.id) {
      setState({
        loading: false,
        permissions: new Set(_cachedPerms.permissions),
        role: _cachedPerms.role,
        roleLabel: _cachedPerms.role_label,
      });
      return;
    }

    if (!_pendingPromise) {
      _pendingPromise = api
        .get("/users/me/permissions")
        .then((r) => r.data)
        .catch(() => ({ permissions: [], role: user.role, role_label: user.role }))
        .finally(() => {
          /* keep promise resolved so cache hit can pick up below */
        });
    }

    try {
      const data = await _pendingPromise;
      _cachedPerms = data;
      _cachedUserId = user.id;
      setState({
        loading: false,
        permissions: new Set(data.permissions || []),
        role: data.role,
        roleLabel: data.role_label,
      });
    } finally {
      _pendingPromise = null;
    }
  }, [user?.id, user?.role]);

  useEffect(() => {
    load();
  }, [load]);

  const can = useCallback(
    (perm) => state.permissions.has(perm),
    [state.permissions]
  );
  const canAny = useCallback(
    (perms) => perms.some((p) => state.permissions.has(p)),
    [state.permissions]
  );
  const canAll = useCallback(
    (perms) => perms.every((p) => state.permissions.has(p)),
    [state.permissions]
  );

  return {
    loading: state.loading,
    role: state.role,
    roleLabel: state.roleLabel,
    permissions: state.permissions,
    can,
    canAny,
    canAll,
    refresh: () => {
      clearPermissionsCache();
      load();
    },
  };
}
